import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
} from 'react';
import { useNavigate } from 'react-router';
import { omit } from 'lodash';
import type {
  GridReadyEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { useShallow } from 'zustand/react/shallow';

import useApplicationsStore from '@/domains/application/applicationStore';
import {
  exportEvaluationsCsv,
  getRunEvaluationApplicationFromEvaluation,
  useEvaluationsList,
} from '@/domains/evaluation';
import RunEvaluation from '@/features/runEvaluation/components/RunEvaluationDialog';
import { clean } from '@/lib/utils';
import { useEvaluationFilters } from '@/portals/application/hooks/useApplicationEvaluationFilters';
import { useMobileRouteLoading, useResponsiveViewport } from '@/shared/hooks';
import useToast from '@/shared/stores/toastStore';
import type { Evaluation } from '@/types';

import ApplicationEvaluationsContent from './ApplicationEvaluationsContent';
import ApplicationEvaluationsFilters from './ApplicationEvaluationsFilters';
import ApplicationEvaluationsPagination from './ApplicationEvaluationsPagination';

const APPLICATION_EVALUATIONS_OPEN_MOBILE_FILTERS_EVENT =
  'evaluations:open-mobile-filters';

interface UseApplicationEvaluationsControllerResult {
  contentProps: ComponentProps<typeof ApplicationEvaluationsContent>;
  filtersProps: ComponentProps<typeof ApplicationEvaluationsFilters>;
  isExporting: boolean;
  isMobileViewport: boolean;
  onExportEvaluations: () => Promise<void>;
  onboardApplicationPath: string;
  paginationProps: ComponentProps<typeof ApplicationEvaluationsPagination>;
  runEvaluationProps: ComponentProps<typeof RunEvaluation>;
}

export const useApplicationEvaluationsController =
  (): UseApplicationEvaluationsControllerResult => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const {
      filters,
      setPage,
      updateFilters,
    } = useEvaluationFilters();
    const isMobileViewport = useResponsiveViewport();
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    const [continuationTokens, setContinuationTokens] = useState<
      Record<number, string>
    >({});
    const [isRunEvaluationOpen, setIsRunEvaluationOpen] = useState(false);
    const [activeEvaluation, setActiveEvaluation] =
      useState<Evaluation | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    const { applications } = useApplicationsStore(
      useShallow((state) => ({
        applications: state.applications,
      })),
    );

    const resetPaginationState = useCallback(() => {
      setContinuationTokens({});
    }, []);

    const handleUpdateFilters = useCallback(
      (
        updates: Partial<
          ComponentProps<typeof ApplicationEvaluationsFilters>['filters']
        >,
      ) => {
        updateFilters(updates);
        resetPaginationState();
      },
      [resetPaginationState, updateFilters],
    );

    const queryFilters = useMemo(
      () => ({
        ...omit(filters, 'applications'),
        bitIds: filters.applications,
        division: filters.division === 'ALL' ? undefined : filters.division,
        latestOnly: true,
        includeCount: true,
        continuationToken: continuationTokens[filters.page] || undefined,
      }),
      [continuationTokens, filters],
    );

    const { data: evaluationsData, isFetching, isLoading } = useEvaluationsList(
      {
        filters: queryFilters,
      },
    );

    useMobileRouteLoading(isLoading || isFetching);

    const filteredApplications = useMemo(() => {
      if (!applications || applications.length === 0) return [];
      if (filters.division === 'ALL') {
        return applications;
      }

      return applications.filter(
        (application) => application.division === filters.division,
      );
    }, [applications, filters.division]);

    const evaluations = evaluationsData?.data.items ?? [];
    const totalCount = evaluationsData?.data?.totalCount ?? 0;
    const pageCount = Math.ceil(totalCount / (filters.limit || 1));
    const runEvaluationApplication =
      getRunEvaluationApplicationFromEvaluation(activeEvaluation);

    useEffect(() => {
      if (evaluationsData?.data.continuationToken) {
        setContinuationTokens((prevState) => ({
          ...prevState,
          [filters.page + 1]: evaluationsData.data.continuationToken,
        }));
      }
    }, [evaluationsData?.data.continuationToken, filters.page]);

    useEffect(() => {
      if (!isMobileViewport && isMobileFiltersOpen) {
        setIsMobileFiltersOpen(false);
      }
    }, [isMobileFiltersOpen, isMobileViewport]);

    useEffect(() => {
      if (typeof window === 'undefined') return;

      const openMobileFilters = () => {
        if (isMobileViewport) {
          setIsMobileFiltersOpen(true);
        }
      };

      window.addEventListener(
        APPLICATION_EVALUATIONS_OPEN_MOBILE_FILTERS_EVENT,
        openMobileFilters,
      );

      return () => {
        window.removeEventListener(
          APPLICATION_EVALUATIONS_OPEN_MOBILE_FILTERS_EVENT,
          openMobileFilters,
        );
      };
    }, [isMobileViewport]);

    const handleGridReady = useCallback(
      (event: GridReadyEvent) => {
        const columnState = event.api.getColumnState();
        event.api.applyColumnState({
          state: columnState.map((column) => {
            const colId = column.colId;
            return {
              ...column,
              sort: colId === filters.sortBy ? filters.sortOrder : null,
            };
          }),
        });
      },
      [filters.sortBy, filters.sortOrder],
    );

    const handleSortChanged = useCallback(
      (event: SortChangedEvent) => {
        const columnState = event.api.getColumnState();
        const sortColumn = columnState.find((column) => column.sort);

        if (sortColumn) {
          handleUpdateFilters({
            sortBy: sortColumn.colId,
            sortOrder: sortColumn.sort === 'asc' ? 'asc' : 'desc',
          });
          return;
        }

        handleUpdateFilters({
          sortBy: 'generatedAt',
          sortOrder: 'desc',
        });
      },
      [handleUpdateFilters],
    );

    const handleNavigateToApplication = useCallback(
      (evaluation: Evaluation) => {
        navigate(
          `/applications/${evaluation.bitId}?env=${evaluation.environmentNames[0]}`,
        );
      },
      [navigate],
    );

    const handleOpenRunEvaluation = useCallback((evaluation: Evaluation) => {
      setIsRunEvaluationOpen(true);
      setActiveEvaluation(evaluation);
    }, []);

    const handleCloseRunEvaluation = useCallback(() => {
      setIsRunEvaluationOpen(false);
      setActiveEvaluation(null);
    }, []);

    const onExportEvaluations = useCallback(async () => {
      if (isExporting) {
        return;
      }

      setIsExporting(true);

      try {
        const baseFilters = clean({
          ...omit(filters, ['applications', 'page', 'latestOnly']),
          bitIds: filters.applications,
          division:
            filters.division === 'ALL' ? undefined : filters.division,
        });

        const response = await exportEvaluationsCsv(baseFilters);

        if (
          response.status === 204 ||
          !response.data ||
          response.data.size === 0
        ) {
          showToast('warning', {
            title: 'No data to export',
            message: 'No evaluations match the current filters.',
          });
          return;
        }

        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        });
        const fileName = `onboarded-applications-${new Date().toISOString()}.csv`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        showToast('error', {
          title: 'Error exporting evaluations',
          message: 'An error occurred while exporting. Please try again.',
        });
      } finally {
        setIsExporting(false);
      }
    }, [filters, isExporting, showToast]);

    return {
      contentProps: {
        evaluations,
        isLoading,
        isMobileViewport,
        onGridReady: handleGridReady,
        onNavigateToApplication: handleNavigateToApplication,
        onOpenRunEvaluation: handleOpenRunEvaluation,
        onSortChanged: handleSortChanged,
      },
      filtersProps: {
        filters,
        filteredApplications,
        isMobileFiltersOpen,
        isMobileViewport,
        onCloseMobileFilters: () => setIsMobileFiltersOpen(false),
        onUpdateFilters: handleUpdateFilters,
      },
      isExporting,
      isMobileViewport,
      onExportEvaluations,
      onboardApplicationPath: '/applications/onboard',
      paginationProps: {
        isMobileViewport,
        limit: filters.limit,
        onLimitChange: (limit) => {
          handleUpdateFilters({ limit });
        },
        onPageChange: setPage,
        page: filters.page,
        pageCount,
        totalCount,
      },
      runEvaluationProps: {
        application: runEvaluationApplication,
        isOpen: isRunEvaluationOpen,
        onClose: handleCloseRunEvaluation,
      },
    };
  };
